import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Layers, MapPin, Sparkles, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AiNotice, EmptyState, MatchRing, PageHeader, ScoreBar, SkillChip, StatusBadge, TableSkeleton,
} from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SelectField } from "@/components/forms/Fields";

const tt = {
  contentStyle: { background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, color: "#0f172a" },
  labelStyle: { color: "#475569", fontSize: 11 },
};

function Ranking() {
  const { meta } = useAuth();
  const navigate = useNavigate();
  const [jobId, setJobId] = useState(null);
  const [result, setResult] = useState(null);
  const [poolOnly, setPoolOnly] = useState("no");

  useEffect(() => {
    if (!jobId && meta?.jobs?.length) setJobId(meta.jobs[0].id);
  }, [meta, jobId]);

  useEffect(() => {
    if (!jobId) return;
    setResult(null);
    api.get(`/ai/job/${jobId}/ranked-candidates`, { params: { limit: 15, pool_only: poolOnly === "yes" } })
      .then((r) => setResult(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [jobId, poolOnly]);

  if (meta && !(meta.jobs || []).length)
    return (
      <EmptyState
        icon={Layers}
        testid="ai-no-jobs-empty"
        title="No open requirements to rank against"
        description="Create a job first — candidate ranking needs a role with required skills and an experience band."
      />
    );


  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row">
        <SelectField label="Requirement" value={jobId} onChange={setJobId} className="max-w-md flex-1" testid="ai-job-select"
          options={(meta?.jobs || []).map((j) => ({ value: j.id, label: `${j.job_code} · ${j.title}` }))} />
        <SelectField label="Search scope" value={poolOnly} onChange={setPoolOnly} className="w-56" testid="ai-pool-filter"
          options={[{ value: "no", label: "All candidates" }, { value: "yes", label: "Talent pool only" }]} />
      </div>

      {!result ? (
        <TableSkeleton rows={6} cols={4} />
      ) : (
        <>
          <div className="surface rounded-md p-5">
            <p className="label-xs mb-3">Required skills for {result.job.title}</p>
            <div className="flex flex-wrap gap-1.5">
              {result.job.required_skills.map((s) => <SkillChip key={s} skill={s} />)}
            </div>
          </div>

          <div className="space-y-3">
            {result.items.map((c, idx) => (
              <article key={c.candidate_id} className="surface surface-hover rounded-md p-5" data-testid="ai-ranked-card">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
                  <div className="flex items-center gap-4 lg:w-72">
                    <span className="font-mono-num w-6 text-sm text-slate-400">{String(idx + 1).padStart(2, "0")}</span>
                    <MatchRing score={c.score} size={64} />
                    <div className="min-w-0">
                      <button onClick={() => navigate(`/candidates/${c.candidate_id}`)} className="block truncate text-sm text-slate-900 hover:text-blue-700">
                        {c.name}
                      </button>
                      <p className="truncate text-[11px] text-slate-500">{c.designation || "Role not available"}</p>
                      <p className="truncate text-[11px] text-slate-400">
                        {c.experience ?? "?"} yrs · {c.location?.split(",")[0] || "Location not available"}
                      </p>
                    </div>
                  </div>

                  <div className="grid flex-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                    {Object.entries(c.breakdown).map(([k, v]) => <ScoreBar key={k} label={k} value={v} />)}
                  </div>

                  <div className="lg:w-64">
                    <p className="label-xs mb-2">Matched</p>
                    <div className="flex flex-wrap gap-1">
                      {c.matched_skills.slice(0, 5).map((s) => <SkillChip key={s} skill={s} tone="matched" />)}
                      {!c.matched_skills.length && <span className="text-[11px] text-slate-400">None</span>}
                    </div>
                    <p className="label-xs mb-2 mt-3">Missing</p>
                    <div className="flex flex-wrap gap-1">
                      {c.missing_skills.slice(0, 4).map((s) => <SkillChip key={s} skill={s} tone="missing" />)}
                      {!c.missing_skills.length && <span className="text-[11px] text-slate-400">None</span>}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <StatusBadge value={c.band} />
                      {c.already_in_pipeline && <span className="text-[10px] text-slate-500">in pipeline</span>}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <AiNotice text={result.disclaimer} />
        </>
      )}
    </div>
  );
}

function Insights() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/ai/insights").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === false) return <EmptyState title="Insights unavailable" />;
  if (!data) return <Skeleton className="h-80 rounded-md bg-slate-100" />;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-2">
        <section className="surface rounded-md p-6">
          <p className="label-xs">Demand vs supply</p>
          <h2 className="mt-1 text-lg font-medium text-slate-900">Skill gaps across open requirements</h2>
          <div className="mt-6 h-72" data-testid="skill-demand-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.skill_demand} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="skill" stroke="#475569" fontSize={10} width={120} tickLine={false} axisLine={false} />
                <Tooltip {...tt} />
                <Bar dataKey="supply" fill="#1d4ed8" radius={[0, 3, 3, 0]} barSize={9} />
                <Bar dataKey="demand" fill="#8b5cf6" radius={[0, 3, 3, 0]} barSize={9} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">
            Purple is open demand across live jobs; blue is available talent in the database.
          </p>
        </section>

        <section className="surface rounded-md p-6">
          <p className="label-xs">Geography</p>
          <h2 className="mt-1 text-lg font-medium text-slate-900">Talent concentration</h2>
          <ul className="mt-6 space-y-3">
            {data.talent_by_location.map((l) => {
              const max = data.talent_by_location[0].candidates || 1;
              return (
                <li key={l.location}>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="inline-flex items-center gap-1.5 text-slate-600">
                      <MapPin className="h-3 w-3 text-slate-500" /> {l.location}
                    </span>
                    <span className="font-mono-num text-slate-500">{l.candidates}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full bg-blue-500" style={{ width: `${(l.candidates / max) * 100}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      <section className="surface rounded-md p-6">
        <p className="label-xs">Bench strength</p>
        <h2 className="mt-1 text-lg font-medium text-slate-900">Most available skills in the database</h2>
        <div className="mt-5 flex flex-wrap gap-2">
          {data.top_supply.map((s) => (
            <span key={s.skill} className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] text-slate-600">
              {s.skill} <span className="font-mono-num text-slate-500">{s.candidates}</span>
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}

function MatchExplorer() {
  const { meta } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [candidateId, setCandidateId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/candidates", { params: { limit: 150 } }).then((r) => setCandidates(r.data.items)).catch(() => {});
  }, []);

  const run = async () => {
    if (!candidateId || !jobId) return toast.error("Pick a candidate and a job");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/match", { candidate_id: candidateId, job_id: jobId });
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="surface flex flex-col gap-3 rounded-md p-5 lg:flex-row lg:items-end">
        <SelectField label="Candidate" value={candidateId} onChange={setCandidateId} className="flex-1" testid="explorer-candidate-select"
          options={candidates.map((c) => ({ value: c.id, label: `${c.full_name} · ${c.current_designation || "Role not available"}` }))} />
        <SelectField label="Job" value={jobId} onChange={setJobId} className="flex-1" testid="explorer-job-select"
          options={(meta?.jobs || []).map((j) => ({ value: j.id, label: `${j.job_code} · ${j.title}` }))} />
        <Button onClick={run} disabled={busy} data-testid="explorer-run-button" className="h-9 gap-2 rounded-full bg-violet-600 px-5 text-xs hover:bg-violet-500">
          <Sparkles className="h-3.5 w-3.5" /> Compute match
        </Button>
      </div>

      {result && (
        <div className="surface rounded-md p-6" data-testid="explorer-result">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="label-xs">Transparent scoring</p>
              <h2 className="mt-1 text-lg font-medium text-slate-900">
                {result.candidate_name} → {result.job_title}
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <MatchRing score={result.overall} />
              <StatusBadge value={result.band} />
            </div>
          </div>
          <div className="mt-6 grid gap-x-10 gap-y-3 sm:grid-cols-2">
            {Object.entries(result.breakdown).map(([k, v]) => <ScoreBar key={k} label={k} value={v} />)}
          </div>
          <div className="mt-6 grid gap-5 lg:grid-cols-3">
            <div>
              <p className="label-xs mb-2">Strong matches</p>
              <div className="flex flex-wrap gap-1">
                {result.matched_skills.length ? result.matched_skills.map((s) => <SkillChip key={s} skill={s} tone="matched" />) : <span className="text-xs text-slate-400">Not available</span>}
              </div>
            </div>
            <div>
              <p className="label-xs mb-2">Missing requirements</p>
              <div className="flex flex-wrap gap-1">
                {result.missing_skills.length ? result.missing_skills.map((s) => <SkillChip key={s} skill={s} tone="missing" />) : <span className="text-xs text-slate-400">None</span>}
              </div>
            </div>
            <div>
              <p className="label-xs mb-2">Potential concerns</p>
              {result.concerns.length ? (
                <ul className="space-y-1.5">
                  {result.concerns.map((c, i) => (
                    <li key={i} className="text-[11px] leading-relaxed text-amber-800/90">{c}</li>
                  ))}
                </ul>
              ) : (
                <span className="text-xs text-slate-400">None flagged</span>
              )}
            </div>
          </div>
          <p className="mt-6 rounded border border-violet-200 bg-violet-50 p-3 text-sm leading-relaxed text-slate-600">
            {result.recommendation}
          </p>
          <AiNotice text={result.disclaimer} />
        </div>
      )}
    </div>
  );
}

export default function AIIntelligence() {
  return (
    <div className="space-y-6">
      <PageHeader
        meta="Recruitment intelligence"
        title="AI Intelligence"
        subtitle="Transparent candidate-job matching, skill gap analysis and talent demand insight. Every score shows its working."
      />

      <Tabs defaultValue="ranking">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-slate-200 bg-white p-1">
          {[["ranking", "Candidate ranking", Layers], ["explorer", "Match explorer", Brain], ["insights", "Talent insights", TrendingUp]].map(([v, label, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`ai-tab-${v}`}
              className="gap-2 rounded px-4 py-1.5 text-xs data-[state=active]:bg-violet-100 data-[state=active]:text-violet-700">
              <Icon className="h-3.5 w-3.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="ranking" className="mt-5"><Ranking /></TabsContent>
        <TabsContent value="explorer" className="mt-5"><MatchExplorer /></TabsContent>
        <TabsContent value="insights" className="mt-5"><Insights /></TabsContent>
      </Tabs>
    </div>
  );
}
